<template>
<section>
    <div class="flex flex-wrap">
        <div
            v-if="label"
            class="text-gray-600 dark:text-white/75 text-xs whitespace-nowrap"
            :class="status === 'error' ? 'text-red-400' : ''">
            {{ label }}
        </div>
        <slot name="afterTitle"></slot>
        <n-popover
            v-if="help"
            trigger="hover"
            class="max-w-[500px]"
            :placement="helpPosition">
            <template #trigger>
                <n-icon size="20" class="text-main mx-2">
                    <QuestionCircle20Filled />
                </n-icon>
            </template>
            <span v-html="help"></span>
        </n-popover>
    </div>
    <n-button
        :size="size"
        tertiary
        class="select-button w-full rounded-md w-full mt-2 pl-2 pr-2"
        @click="showDrawer = true">
        <div class="w-full flex justify-between items-center">
            <div
                v-if="value"
                class="flex items-center w-[calc(100%-24px)] overflow-x-auto scrollbar-hide"
                :class="multiple ? '' : ''">
                <template v-if="multiple">
                    <div
                        v-for="checked in currentValue"
                        :key="checked"
                        class="rounded-md h-full bg-main bg-opacity-50 border-opacity-50 border border-main p-1 mr-2">
                        {{ checked.label }}
                    </div>
                </template>
                <template v-else>
                    <span class="text-gray-600 dark:text-white/75 text-xs pl-2">{{ currentValue?.label }}</span>
                </template>
            </div>
            <span v-else
                class="text-gray-600/50 dark:text-white/40">{{ placeholder }}</span>
            <n-icon size="18"><CaretDown12Filled /></n-icon>
        </div>
    </n-button>
    <n-drawer
        v-model:show="showDrawer"
        width="100%"
        display-directive="show"
        class="bg-gray-100 dark:bg-darkbg"
        @after-enter="showDrawerContent = true">
        <n-drawer-content body-content-style="padding: 0">
            <div class="flex flex-col h-full">
                <div class="w-full flex justify-between px-3 items-center bg-gray-100 dark:bg-darkbg text-sm text-center text-gray-600 dark:text-white/75 py-2">
                    <n-icon
                        size="20"
                        class="text-gray-600 dark:text-white/75"
                        @click="showDrawer = false">
                        <ArrowLeft12Regular />
                    </n-icon>
                    <rb-input v-if="showInputQuery" v-model:value="query" class="ml-3" />
                    <n-button
                        quaternary
                        class="bg-transparent text-main pr-0"
                        @click="showDrawer = false">
                        {{ t('done') }}
                    </n-button>
                </div>
                <div class="pb-4 flex-grow overflow-y-scroll overscroll-none scrollbar-hide">
                    <template
                        v-for="(item, i) in optionsRef"
                        :key="i">
                        <template v-if="item.type === 'group'">
                            <div
                                v-if="item.show"
                                class="text-gray-600 dark:text-white/75 sticky top-0 text-sm pl-3 py-2 bg-white dark:bg-darkCardBg z-50 opacity-100">
                                {{ item.label }}
                            </div>
                            <template
                                v-for="child in item.children"
                                :key="child.value">
                                <div
                                    v-if="child.show"
                                    class="w-full flex justify-between items-center pl-6 pr-3 py-2 rounded-none"
                                    @click="setValue(child)">
                                    {{ child.label }}
                                    <n-icon v-if="isChecked(child)" size="20" class="text-main">
                                        <Checkmark12Filled />
                                    </n-icon>
                                </div>
                            </template>
                        </template>
                        <template v-else>
                            <div
                                v-if="item.show"
                                class="w-full flex justify-between items-center pl-6 pr-3 py-2 rounded-none dark:odd:bg-[#242424] odd:bg-gray-50"
                                @click="setValue(item)">
                                {{ item.label }}
                                <n-icon v-if="isChecked(item)" size="20" class="text-main">
                                    <Checkmark12Filled />
                                </n-icon>
                            </div>
                        </template>
                        <!-- dark:odd:bg-[#242424] odd:bg-gray-50 -->
                        
                    </template>
                </div>
            </div>
        </n-drawer-content>
    </n-drawer>
</section>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';

// i18n
import { useI18n } from 'vue-i18n';

// naive-ui
import {
    NIcon,
    NSelect,
    NButton,
    NDrawer,
    NPopover,
    NDrawerContent } from 'naive-ui';

// components
import RbInput from '@components/rb-input/mobile.vue';

// icons
import {
    Checkmark12Filled,
    CaretDown12Filled,
    ArrowLeft12Regular,
    QuestionCircle20Filled } from '@vicons/fluent';

export default {
    name: 'rb-checkbox-list',
    emits: ['update:value'],
    props: {
        label: {
            type: String,
        },
        labelWidth: {
            type: [ String, Number ],
        },
        labelPosition: {
            type: String,
            default: 'top',
            validator: prop => ['left', 'top'].includes(prop),
        },
        size: {
            type: String,
            default: 'medium',
            validator: prop => ['tiny', 'small', 'medium', 'large'].includes(prop),
        },
        help: {
            type: String,
        },
        largeHelp: {
            type: Boolean,
        },
        helpPosition: {
            type: String,
            default: 'right',
        },
        value: {
            required: true,
        },
        options: {
            type: Array,
            required: true,
        },
        placeholder: {
            type: String,
            default: () => useI18n().t('select'),
        },
        clearable: {
            type: Boolean,
        },
        multiple: {
            type: Boolean,
        },
        defaultValue: {
            type: Boolean,
            default: -1,
        },
        loading: {
            type: Boolean,
        },
        hideSelect: {
            type: Boolean,
        },
        status: {
            type: String,
            default: undefined,
        },
        msg: {
            type: String,
        },
        disabled: {
            type: Boolean,
        },
    },
    components: {
        NIcon,
        RbInput,
        NDrawer,
        NSelect,
        NButton,
        NPopover,
        NDrawerContent,
        Checkmark12Filled,
        CaretDown12Filled,
        ArrowLeft12Regular,
        QuestionCircle20Filled,
    },
    setup(props, { emit }) {
        // vars
        const { _ } = window;

        // i18n
        const { t } = useI18n();
        const query = ref('');
        const showInputQuery = ref(false);
        const showDrawer = ref(false);
        const localValue = ref([]);

        watch(showDrawer, v => {
            if (v) {
                setTimeout(() => {
                    showInputQuery.value = true;
                });
            }
        });

        const optionsRef = computed(() => {
            const res = props.options.map(el => {
                if (el.type === 'group') {
                    el.children.forEach(child => {
                        child.show = child.label.toLowerCase().includes(query.value.toLowerCase());
                    });

                    el.show = el.children.some(child => child.show);
                } else {
                    el.show = el.label.toLowerCase().includes(query.value.toLowerCase());
                }
                
                return el;
            });

            return res;
        });

        const currentValue = computed(() => {
            if (props.multiple) {
                const result = [];

                for (const item of optionsRef.value) {
                    if (item.value != props.value && Object.prototype.hasOwnProperty.call(item, 'children')) {
                        const child = item.children.find(({ value }) => {
                            if (Array.isArray(props.value))
                                return ~props.value.findIndex(el => el == value);
                        });

                        if (child) {
                            result.push(child);
                        }
                    } else if (Array.isArray(props.value) && ~props.value.findIndex(el => el == item.value)) {
                        result.push(item);
                    }
                }

                return result;
            } else {
                let result;

                for (const item of optionsRef.value) {
                    if (item.value != props.value && Object.prototype.hasOwnProperty.call(item, 'children')) {
                        const child = item.children.find(({ value }) => value == props.value);

                        if (child) {
                            result = child;
                            break;
                        }
                    } else if (item.value == props.value) {
                        result = item;
                        break;
                    }
                }

                return result;
            }
        });

        const addOrRemove = (array, value) => {
            var index = array.indexOf(value);

            if (index === -1) {
                array.push(value);
            } else {
                array.splice(index, 1);
            }

            return array;
        };

        const setValue = v => {
            if (props.multiple) {
                const copy = Array.isArray(props.value)
                    ? _.cloneDeep(props.value)
                    : [ props.value ];

                emit('update:value', addOrRemove(copy, v.value));
            } else {
                emit('update:value', v.value);
            }
        };

        const isChecked = v => {
            return Array.isArray(props.value)
                ? ~props.value.findIndex(el => el == v.value)
                : props.value == v.value;
        };

        return {
            query,
            showDrawer,
            localValue,
            optionsRef,
            currentValue,
            showInputQuery,
            t,
            setValue,
            isChecked,
        };
    },
};
</script>

<style lang="scss" scoped>
.select-button :deep(.n-button__content) {
    width: 100%;
}
</style>